import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError, fileDownloadUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  ArrowLeft, Calendar, Coins, Flag, User, Tag, Paperclip, Send,
  Check, X, MessageSquare, Building2, Lock, Pencil, AlertTriangle, CheckCircle2, Users,
} from "lucide-react";
import { initials, statusLabel, timeAgo, formatDate, isOwner, isDeptAdmin } from "@/lib/utils";
import { toast } from "sonner";

const DIFFICULTIES = [
  { value: "easy", label: "Easy", credits: 100 },
  { value: "medium", label: "Medium", credits: 300 },
  { value: "hard", label: "Hard", credits: 600 },
  { value: "expert", label: "Expert", credits: 1000 },
];

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [r, setR] = useState(null);
  const [solText, setSolText] = useState("");
  const [solLinks, setSolLinks] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewFeedback, setReviewFeedback] = useState("");
  const [awardCredits, setAwardCredits] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [categories, setCategories] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [peoplePickerOpen, setPeoplePickerOpen] = useState(false);
  const [approveBounty, setApproveBounty] = useState("");
  const [rejectBountyFeedback, setRejectBountyFeedback] = useState("");
  const [approving, setApproving] = useState(false);

  const load = () => api.get(`/requests/${id}`).then((res) => setR(res.data));
  useEffect(() => { load(); }, [id]);

  const claimerIds = (r?.claimers || []).map((c) => c.id);
  const isClaimer = !!user && claimerIds.includes(user.id);

  useEffect(() => {
    if (!r || !user || !isClaimer) return;
    const existing = (r.solutions || []).find(
      (s) => s.contributor_id === user.id && ["pending", "changes_requested", "withdrawn"].includes(s.status)
    );
    if (!existing) return;
    setSolText(existing.submission_text || "");
    setSolLinks((existing.links || []).join(", "));
  }, [r?.id, isClaimer, user?.id]);

  if (!r) return <div className="font-mono text-sm text-slate-500">Loading…</div>;

  const isCreator = user?.id === r.creator_id;
  const ticketAdmin = !!r.is_ticket_admin || isCreator || isOwner(user)
    || (isDeptAdmin(user) && user?.department === r.department);
  const isPendingApproval = r.status === "pending_approval";
  const canApproveBounty = isPendingApproval && !isCreator
    && (isOwner(user) || (isDeptAdmin(user) && user?.department === r.department));
  const isExpired = r.status === "expired";
  const isClosed = ["completed", "rejected", "expired"].includes(r.status);
  const mySolution = r.solutions?.find(
    (s) => s.contributor_id === user?.id && ["pending", "changes_requested", "withdrawn"].includes(s.status)
  );
  const hasDecided = r.solutions?.some(
    (s) => s.contributor_id === user?.id && ["approved", "rejected"].includes(s.status)
  );
  const canClaim = !isOwner(user) && !isCreator && !isClaimer && !hasDecided && ["open", "claimed"].includes(r.status);
  const claimBlockedByReview = !isOwner(user) && !isCreator && !isClaimer && hasDecided && ["open", "claimed"].includes(r.status);
  const canSubmit = !isOwner(user) && isClaimer && !isExpired && !isClosed && !hasDecided && !isPendingApproval;
  const canReview = ticketAdmin && !isPendingApproval && r.solutions?.some(
    (s) => s.status === "pending" && s.contributor_id !== user?.id
  );
  const canComplete = ticketAdmin && !isClosed && !isExpired && !isPendingApproval;
  const canEditTicket = (isCreator || isOwner(user)) || (ticketAdmin && !isPendingApproval);

  const onClaim = async () => {
    try {
      await api.post(`/requests/${id}/claim`);
      toast.success("Request claimed");
      load();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(msg.includes("verify") ? `${msg} Use the banner above to resend.` : msg);
    }
  };

  const onUnclaim = async () => {
    try { await api.post(`/requests/${id}/unclaim`); toast.success("Released"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const onUpload = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data);
      }
      setFiles((prev) => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} file(s) attached`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onSubmitSolution = async () => {
    if (!solText.trim()) return toast.error("Add a note describing your solution");
    setSubmitting(true);
    try {
      await api.post(`/requests/${id}/solutions`, {
        submission_text: solText,
        links: solLinks.split(",").map((s) => s.trim()).filter(Boolean),
        file_ids: files.map((f) => f.id),
      });
      toast.success("Solution submitted");
      setSolText(""); setSolLinks(""); setFiles([]);
      load();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(msg.includes("verify") ? `${msg} Use the banner above to resend.` : msg);
    }
    finally { setSubmitting(false); }
  };

  const openReview = (s) => {
    setReviewTarget(s);
    setReviewFeedback("");
    setAwardCredits(r.bounty_credits);
    setReviewOpen(true);
  };

  const onReview = async (action) => {
    if (!reviewTarget) return;
    try {
      const body = { action, feedback: reviewFeedback };
      if (action === "approve") body.award_credits = Number(awardCredits);
      await api.post(`/solutions/${reviewTarget.id}/review`, body);
      toast.success(
        action === "approve" ? "Approved — credits transferred" :
        action === "reject" ? "Rejected" : "Changes requested"
      );
      setReviewOpen(false); setReviewFeedback(""); setReviewTarget(null);
      load();
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(msg.includes("verify") ? `${msg} Use the banner above to resend.` : msg);
    }
  };

  const onComplete = async () => {
    if (!window.confirm("Complete this ticket? No new solutions can be submitted and any pending submissions will be rejected.")) return;
    try {
      await api.post(`/requests/${id}/complete`);
      toast.success("Ticket completed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const onBountyDecision = async (action) => {
    setApproving(true);
    try {
      const payload = { action };
      if (action === "approve") {
        payload.bounty_credits = Number(approveBounty || r.bounty_credits);
      } else {
        const feedback = (rejectBountyFeedback || "").trim();
        if (!feedback) {
          toast.error("Add a rejection message for the creator");
          setApproving(false);
          return;
        }
        payload.feedback = feedback;
      }
      await api.post(`/requests/${id}/approve-bounty`, payload);
      toast.success(action === "approve" ? "Ticket published" : "Bounty request rejected");
      setRejectBountyFeedback("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setApproving(false);
    }
  };

  const openEdit = () => {
    if (categories.length === 0) {
      api.get("/org/categories").then((res) => setCategories(res.data)).catch(() => {});
    }
    if (orgUsers.length === 0) {
      api.get("/users").then((res) => setOrgUsers(res.data)).catch(() => {});
    }
    if (isOwner(user) && departments.length === 0) {
      api.get("/org/departments").then((res) => setDepartments(res.data)).catch(() => {});
    }
    const creatorId = r.creator_id;
    const allowed = (r.allowed_user_ids || []).filter((uid) => uid !== creatorId);
    setEditForm({
      title: r.title || "",
      description: r.description || "",
      category: r.category || "",
      tags: (r.tags || []).join(", "),
      difficulty: r.difficulty || "medium",
      bounty_credits: r.bounty_credits ?? 0,
      due_date: r.due_date ? r.due_date.slice(0, 10) : "",
      visibility: r.visibility === "specific" ? "specific" : "org",
      allowed_user_ids: allowed,
      department: r.department || "",
    });
    setEditOpen(true);
  };

  const selectableUsers = orgUsers.filter(
    (u) => u.role !== "owner" && u.id !== user?.id && u.id !== r?.creator_id,
  );
  const selectedPeopleIds = (editForm?.allowed_user_ids || []).filter(
    (id) => selectableUsers.some((u) => u.id === id)
  );

  const toggleEditUser = (uid) => {
    setEditForm((f) => {
      if (!f) return f;
      const has = f.allowed_user_ids.includes(uid);
      return {
        ...f,
        allowed_user_ids: has
          ? f.allowed_user_ids.filter((x) => x !== uid)
          : [...f.allowed_user_ids, uid],
      };
    });
  };

  const setEditDifficulty = (v) => {
    const d = DIFFICULTIES.find((x) => x.value === v);
    setEditForm((f) => ({
      ...f,
      difficulty: v,
      bounty_credits: d?.credits ?? f.bounty_credits,
    }));
  };

  const onSaveEdit = async () => {
    if (!editForm.title.trim() || !editForm.description.trim()) {
      return toast.error("Title and description are required");
    }
    if (editForm.visibility === "specific" && selectedPeopleIds.length === 0) {
      return toast.error("Select at least one person, or make the request open to everyone.");
    }
    setSavingEdit(true);
    try {
      const payload = {
        title: editForm.title,
        description: editForm.description,
        category: editForm.category,
        tags: editForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        difficulty: editForm.difficulty,
        bounty_credits: Number(editForm.bounty_credits),
        due_date: editForm.due_date ? new Date(editForm.due_date).toISOString() : null,
        visibility: editForm.visibility === "specific" ? "specific" : "org",
        allowed_user_ids: editForm.visibility === "specific" ? selectedPeopleIds : [],
      };
      if (isOwner(user) && editForm.department) {
        payload.department = editForm.department;
      }
      const { data: updated } = await api.patch(`/requests/${id}`, payload);
      toast.success(
        updated?.status === "pending_approval"
          ? "Bounty is above department max — sent for approval again"
          : "Ticket updated",
      );
      setEditOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="detail-back">
        <ArrowLeft className="h-4 w-4" /> Back to board
      </button>

      <div className="grid lg:grid-cols-[1fr_320px] gap-8">
        {/* Main */}
        <div className="space-y-6">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge variant="outline">{r.category}</Badge>
              <span className={`pill pill-${r.status}`}>{statusLabel(r.status)}</span>
              <span className={`font-mono text-xs font-semibold diff-${r.difficulty}`}>· {r.difficulty?.toUpperCase()}</span>
              {r.visibility === "specific" && (
                <Badge variant="secondary" className="gap-1" data-testid="private-badge">
                  <Lock className="h-3 w-3" /> Private
                </Badge>
              )}
              {canEditTicket && (
                <Button size="sm" variant="outline" className="ml-auto" onClick={openEdit} data-testid="edit-request-btn">
                  <Pencil className="h-4 w-4 mr-1" /> Edit ticket
                </Button>
              )}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter mt-3" data-testid="request-title">{r.title}</h1>
            <p className="mt-4 text-slate-700 whitespace-pre-wrap leading-relaxed">{r.description}</p>
            {r.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {r.tags.map((t) => (
                  <span key={t} className="text-xs font-mono text-slate-600 bg-slate-100 border border-slate-200 px-2 py-1 rounded">#{t}</span>
                ))}
              </div>
            )}
          </div>

          {isPendingApproval && (
            <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 space-y-3" data-testid="pending-approval-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                <div className="text-sm text-orange-900">
                  <div className="font-semibold">Awaiting bounty approval</div>
                  <p>
                    Requested <span className="font-mono font-semibold">{r.requested_bounty_credits || r.bounty_credits}</span> credits.
                    Admin Approval required : Bounty exceeds department limit. This ticket is not visible on the public board until approved.
                  </p>
                </div>
              </div>
              {canApproveBounty && (
                <div className="space-y-3 pl-8">
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Approved bounty</Label>
                      <Input
                        type="number"
                        min="0"
                        className="w-32"
                        data-testid="approve-bounty-input"
                        value={approveBounty || r.bounty_credits}
                        onChange={(e) => setApproveBounty(e.target.value)}
                      />
                    </div>
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      disabled={approving}
                      onClick={() => onBountyDecision("approve")}
                      data-testid="approve-bounty-btn"
                    >
                      <Check className="h-4 w-4 mr-1" /> Approve & publish
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Rejection message (required to reject)</Label>
                    <Textarea
                      rows={2}
                      placeholder="Explain why this bounty request is rejected…"
                      data-testid="reject-bounty-feedback"
                      value={rejectBountyFeedback}
                      onChange={(e) => setRejectBountyFeedback(e.target.value)}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={approving}
                      onClick={() => onBountyDecision("reject")}
                      data-testid="reject-bounty-btn"
                    >
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {r.status === "rejected" && r.approval_feedback && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 space-y-1" data-testid="bounty-rejection-notice">
              <div className="font-semibold text-sm text-rose-900">Bounty request rejected</div>
              <p className="text-sm text-rose-800 whitespace-pre-wrap">{r.approval_feedback}</p>
            </div>
          )}

          {isExpired && (
            <div className="rounded-xl border border-slate-300 bg-slate-50 p-4 flex items-start gap-3" data-testid="expired-notice">
              <AlertTriangle className="h-5 w-5 text-slate-500 mt-0.5" />
              <div className="text-sm text-slate-700">
                <div className="font-semibold">This request has expired</div>
                <p>The due date has passed. New claims and solutions are no longer accepted.</p>
              </div>
            </div>
          )}

          {hasDecided && !isClosed && !isExpired && (
            <div className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 flex items-start gap-3" data-testid="reviewed-notice">
              <Check className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-slate-700">
                <div className="font-semibold">Your submission was reviewed</div>
                <p>See the reviewer's decision below. This request is no longer accepting a new submission from you.</p>
              </div>
            </div>
          )}

          {/* Submit solution */}
          {canSubmit && (
            <Card className="border-blue-200 bg-blue-50/30">
              <CardContent className="p-6">
                <h3 className="font-display text-xl font-semibold tracking-tight mb-1">
                  {mySolution ? "Edit your solution" : "Submit your solution"}
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  {mySolution
                    ? "Update your submission before sending for review again."
                    : "Share proof — links, notes, attachments. The creator will review."}
                </p>
                <div className="space-y-3">
                  <Textarea
                    rows={5}
                    placeholder="What did you do? Who did you connect them with? Add context."
                    value={solText}
                    onChange={(e) => setSolText(e.target.value)}
                    data-testid="solution-text"
                  />
                  <Input
                    placeholder="Links (comma separated)"
                    value={solLinks}
                    onChange={(e) => setSolLinks(e.target.value)}
                    data-testid="solution-links"
                  />
                  <div>
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer hover:text-blue-600">
                      <Paperclip className="h-4 w-4" />
                      <span>{uploading ? "Uploading…" : "Attach files"}</span>
                      <input type="file" multiple onChange={onUpload} className="hidden" data-testid="solution-files" />
                    </label>
                    {files.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {files.map((f) => (
                          <span key={f.id} className="text-xs bg-white border border-slate-200 rounded px-2 py-1 font-mono">{f.filename}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={onSubmitSolution} disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-solution-btn">
                      <Send className="h-4 w-4 mr-2" />
                      {submitting ? "Submitting…" : mySolution ? "Update submission" : "Submit for review"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Solutions list */}
          <div>
            <h3 className="font-display text-xl font-semibold tracking-tight mb-3 flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Submissions <span className="text-slate-400 font-mono text-sm">{r.solutions?.length || 0}</span>
            </h3>
            <div className="space-y-3">
              {(r.solutions || []).map((s) => (
                <Card key={s.id} className="border-slate-200" data-testid={`solution-${s.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={s.contributor?.avatar_url || ""} />
                          <AvatarFallback>{initials(s.contributor?.name || "?")}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="text-sm font-semibold">{s.contributor?.name}</div>
                          <div className="text-xs text-slate-500">{s.contributor?.designation} · {timeAgo(s.submitted_at)}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {s.status === "approved" && typeof s.awarded_credits === "number" && (
                          <span className="font-mono text-xs font-bold text-emerald-600">+{s.awarded_credits}c</span>
                        )}
                        <Badge variant={s.status === "approved" ? "default" : s.status === "rejected" ? "destructive" : "outline"} className="text-[10px]">
                          {s.status.replace("_", " ")}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{s.submission_text}</p>
                    {s.links?.length > 0 && (
                      <div className="mt-3 space-y-1">
                        {s.links.map((l, i) => (
                          <a key={i} href={l} target="_blank" rel="noreferrer" className="block text-xs text-blue-600 hover:underline truncate">↗ {l}</a>
                        ))}
                      </div>
                    )}
                    {s.file_ids?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {s.file_ids.map((fid) => (
                          <a key={fid} href={fileDownloadUrl(fid)} target="_blank" rel="noreferrer" className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-1 hover:bg-slate-200">
                            <Paperclip className="h-3 w-3 inline mr-1" /> Attachment
                          </a>
                        ))}
                      </div>
                    )}
                    {s.feedback && (
                      <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700">
                        <span className="font-semibold">Reviewer feedback:</span> {s.feedback}
                      </div>
                    )}
                    {s.status === "pending" && ticketAdmin && s.contributor_id !== user?.id && (
                      <div className="flex gap-2 mt-4 pt-3 border-t border-slate-200">
                        <Button size="sm" onClick={() => openReview(s)} data-testid={`review-${s.id}`} className="bg-emerald-600 hover:bg-emerald-700">
                          <Check className="h-4 w-4 mr-1" /> Review
                        </Button>
                      </div>
                    )}
                    {s.status === "pending" && ticketAdmin && s.contributor_id === user?.id && (
                      <p className="mt-4 pt-3 border-t border-slate-200 text-xs text-slate-500">
                        You cannot review your own solution — another ticket admin must approve it.
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
              {(!r.solutions || r.solutions.length === 0) && (
                <div className="text-sm text-slate-500 text-center py-8 border-2 border-dashed border-slate-200 rounded-xl">
                  No submissions yet.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4 lg:sticky lg:top-24 self-start">
          <Card className="border-slate-200">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Bounty</span>
                <Coins className="h-4 w-4 text-amber-500" />
              </div>
              <div className="font-display text-4xl font-bold tracking-tighter text-blue-600">
                {r.bounty_credits}<span className="text-base text-slate-500 ml-1">credits</span>
              </div>

              <div className="space-y-3 pt-3 border-t border-slate-200">
                <Detail icon={User} label="Creator">
                  <Link to={`/app/profile/${r.creator?.id}`} className="hover:text-blue-600 inline-flex items-center gap-2">
                    <Avatar className="h-5 w-5"><AvatarImage src={r.creator?.avatar_url || ""} /><AvatarFallback className="text-[8px]">{initials(r.creator?.name || "?")}</AvatarFallback></Avatar>
                    {r.creator?.name}
                  </Link>
                </Detail>
                <Detail icon={User} label={`Claimed by (${r.claimers?.length || 0})`}>
                  {r.claimers?.length > 0 ? (
                    <div className="flex flex-col items-end gap-1">
                      {r.claimers.map((c) => (
                        <Link key={c.id} to={`/app/profile/${c.id}`} className="hover:text-blue-600 inline-flex items-center gap-2">
                          <Avatar className="h-5 w-5"><AvatarImage src={c.avatar_url || ""} /><AvatarFallback className="text-[8px]">{initials(c.name)}</AvatarFallback></Avatar>
                          {c.name}
                        </Link>
                      ))}
                    </div>
                  ) : <span className="text-slate-400">—</span>}
                </Detail>
                <Detail icon={Building2} label="Department">{r.department || "—"}</Detail>
                <Detail icon={Flag} label="Difficulty"><span className={`diff-${r.difficulty} font-semibold uppercase font-mono text-xs`}>{r.difficulty}</span></Detail>
                <Detail icon={Calendar} label="Created">{formatDate(r.created_at)}</Detail>
                {r.due_date && <Detail icon={Calendar} label="Due">{formatDate(r.due_date)}</Detail>}
                <Detail icon={Tag} label="Category">{r.category}</Detail>
              </div>

              <div className="pt-3 border-t border-slate-200 space-y-2">
                {canClaim && (
                  <Button onClick={onClaim} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="claim-btn">
                    Claim this request
                  </Button>
                )}
                {claimBlockedByReview && (
                  <div className="space-y-1">
                    <Button disabled className="w-full" data-testid="claim-btn-disabled">
                      Claim this request
                    </Button>
                    <p className="text-xs text-slate-500 text-center">
                      Your solution was already reviewed — you cannot reclaim this ticket.
                    </p>
                  </div>
                )}
                {isClaimer && !isClosed && (
                  <Button onClick={onUnclaim} variant="outline" className="w-full" data-testid="unclaim-btn">
                    Release claim
                  </Button>
                )}
                {canComplete && (
                  <Button onClick={onComplete} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="complete-ticket-btn">
                    <CheckCircle2 className="h-4 w-4 mr-2" /> Complete ticket
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>

      {/* Review dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review submission</DialogTitle>
            <DialogDescription>
              Set the reward for {reviewTarget?.contributor?.name} (max {" "}
              <span className="font-mono font-semibold">{r.bounty_credits} credits</span>).
              Approving pays this contributor and keeps the ticket open — use “Complete ticket” to close it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider">Award (credits)</Label>
            <Input
              type="number"
              min="0"
              max={r.bounty_credits}
              value={awardCredits}
              onChange={(e) => setAwardCredits(e.target.value)}
              data-testid="review-award"
            />
          </div>
          <Textarea
            rows={4}
            placeholder="Optional feedback for the contributor…"
            value={reviewFeedback}
            onChange={(e) => setReviewFeedback(e.target.value)}
            data-testid="review-feedback"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => onReview("request_changes")} data-testid="review-request-changes">Request changes</Button>
            <Button variant="destructive" onClick={() => onReview("reject")} data-testid="review-reject">
              <X className="h-4 w-4 mr-1" /> Reject
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onReview("approve")} data-testid="review-approve">
              <Check className="h-4 w-4 mr-1" /> Approve & pay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit ticket</DialogTitle>
            <DialogDescription>Update the same fields you set when creating this request.</DialogDescription>
          </DialogHeader>
          {editForm && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Title</Label>
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  data-testid="edit-title"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Description</Label>
                <Textarea
                  rows={5}
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  data-testid="edit-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium uppercase tracking-wider">Category</Label>
                  <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                    <SelectTrigger data-testid="edit-category"><SelectValue placeholder="Choose…" /></SelectTrigger>
                    <SelectContent>
                      {categories.length === 0 && editForm.category && (
                        <SelectItem value={editForm.category}>{editForm.category}</SelectItem>
                      )}
                      {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium uppercase tracking-wider">Difficulty</Label>
                  <Select value={editForm.difficulty} onValueChange={setEditDifficulty}>
                    <SelectTrigger data-testid="edit-difficulty"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DIFFICULTIES.map((d) => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {isOwner(user) ? (
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium uppercase tracking-wider">Department</Label>
                  <Select
                    value={editForm.department}
                    onValueChange={(v) => setEditForm({ ...editForm, department: v })}
                  >
                    <SelectTrigger data-testid="edit-department"><SelectValue placeholder="Required" /></SelectTrigger>
                    <SelectContent>
                      {departments.length === 0 && editForm.department && (
                        <SelectItem value={editForm.department}>{editForm.department}</SelectItem>
                      )}
                      {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-slate-500 leading-snug">
                    Moving departments transfers the reserved bounty to the new department pool.
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium uppercase tracking-wider">Department</Label>
                  <Input value={editForm.department || ""} disabled className="bg-slate-50" data-testid="edit-department" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Tags (comma separated)</Label>
                <Input
                  value={editForm.tags}
                  onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                  data-testid="edit-tags"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium uppercase tracking-wider">Bounty (credits)</Label>
                  <Input
                    type="number" min="0"
                    value={editForm.bounty_credits}
                    onChange={(e) => setEditForm({ ...editForm, bounty_credits: e.target.value })}
                    data-testid="edit-bounty"
                  />
                  <p className="text-[11px] text-slate-500 leading-snug">
                    If bounty is above the department max per ticket, it will need approval again.
                  </p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium uppercase tracking-wider">Due date</Label>
                  <Input
                    type="date"
                    value={editForm.due_date}
                    onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                    data-testid="edit-duedate"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-xs font-medium uppercase tracking-wider">Who can see this?</Label>
                <RadioGroup
                  value={editForm.visibility === "specific" ? "specific" : "org"}
                  onValueChange={(v) => setEditForm({ ...editForm, visibility: v })}
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  <label
                    htmlFor="edit-vis-org"
                    className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${editForm.visibility !== "specific" ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <RadioGroupItem value="org" id="edit-vis-org" className="mt-0.5" data-testid="edit-visibility-org" />
                    <div>
                      <div className="flex items-center gap-1.5 font-medium text-sm"><Users className="h-4 w-4" /> Open to everyone</div>
                      <p className="text-xs text-slate-500 mt-0.5">All org members can see and claim this.</p>
                    </div>
                  </label>
                  <label
                    htmlFor="edit-vis-specific"
                    className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors ${editForm.visibility === "specific" ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <RadioGroupItem value="specific" id="edit-vis-specific" className="mt-0.5" data-testid="edit-visibility-specific" />
                    <div>
                      <div className="flex items-center gap-1.5 font-medium text-sm"><Lock className="h-4 w-4" /> Specific people</div>
                      <p className="text-xs text-slate-500 mt-0.5">Only people you pick (plus you) can see and claim.</p>
                    </div>
                  </label>
                </RadioGroup>

                {editForm.visibility === "specific" && (
                  <div className="space-y-2">
                    <Popover open={peoplePickerOpen} onOpenChange={setPeoplePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="w-full justify-start font-normal" data-testid="edit-people-picker">
                          <Users className="h-4 w-4 mr-2" />
                          {selectedPeopleIds.length > 0
                            ? `${selectedPeopleIds.length} ${selectedPeopleIds.length === 1 ? "person" : "people"} selected`
                            : "Select people…"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search people…" />
                          <CommandList>
                            <CommandEmpty>No people found.</CommandEmpty>
                            <CommandGroup>
                              {selectableUsers.map((u) => {
                                const selected = editForm.allowed_user_ids.includes(u.id);
                                return (
                                  <CommandItem
                                    key={u.id}
                                    value={`${u.name} ${u.email}`}
                                    onSelect={() => toggleEditUser(u.id)}
                                  >
                                    <Check className={`h-4 w-4 ${selected ? "opacity-100" : "opacity-0"}`} />
                                    <div className="flex flex-col">
                                      <span className="text-sm">{u.name}</span>
                                      <span className="text-xs text-slate-500">{u.email}{u.department ? ` · ${u.department}` : ""}</span>
                                    </div>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>

                    {selectedPeopleIds.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {selectedPeopleIds.map((pid) => {
                          const u = selectableUsers.find((x) => x.id === pid);
                          return (
                            <Badge key={pid} variant="secondary" className="gap-1 pr-1">
                              {u ? u.name : pid}
                              <button
                                type="button"
                                onClick={() => toggleEditUser(pid)}
                                className="rounded-full hover:bg-slate-300/60 p-0.5"
                                aria-label={`Remove ${u ? u.name : pid}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={onSaveEdit} disabled={savingEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-save">
              {savingEdit ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Detail = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3 text-sm">
    <Icon className="h-4 w-4 text-slate-400 mt-0.5" />
    <div className="flex-1 flex justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right">{children}</span>
    </div>
  </div>
);
